import { useEffect, useState } from "react";
import { isMobileScreen } from "@/utils/functions";
import { IS_LIVE } from "@/utils/constants";
import Theme from "@/components/Theme";
import Hero from "@/organisms/Hero";
import Slider from "@/components/organisms/Slider";
import AudioLayout from "@/organisms/AudioLayout";
import Discover from "@/organisms/Discover";
import Trustpilot from "@/organisms/Trustpilot";
import FeatureSectionTwocol from "@/organisms/FeatureSectionTwocol";
import ToolsSection from "@/components/organisms/ToolsSection";
import ProgressBarSwiper from "@/components/organisms/ProgressBarSwiper";

export default function Tools() {
  const [isMobile, setIsMobile] = useState(null);

  useEffect(() => {
    setIsMobile(isMobileScreen);
  }, []);
  const seoData = {
    indexing: true,
    title: "AnswerForce: explore the tech toolkit, made for small businesses",
    description:
      "24/7 live answering services with a full tech-toolkit. Human support, powered by technical solutions. Discover the AnswerForce difference.",
    og: {
      title: "AnswerForce: explore the tech toolkit, made for small businesses",
      description:
        "24/7 live answering services with a full tech-toolkit. Human support, powered by technical solutions. Discover the AnswerForce difference.",
      url: "https://www.answerforce.com/tools",
      image:
        "https://assets.answerforce.com/answerforce/us/website/images/og/tools-og.jpg",
    },
    twitter: {
      title: "AnswerForce: explore the tech toolkit, made for small businesses",
      description:
        "24/7 live answering services with a full tech-toolkit. Human support, powered by technical solutions. Discover the AnswerForce difference.",
      url: "https://www.answerforce.com/tools",
      image:
        "https://assets.answerforce.com/answerforce/us/website/images/og/tools-og.jpg",
      image_alt: "AnswerForce tools page",
    },
  };

  const heroImgData = {
    fileName: "tools-hero",
    alt: "Compete with the best in the business",
  };

  const hero = (
    <Hero
      heroTitle="Human connection<br class='desktop-only' /> powered by tech<span class='dot'>.</span>"
      heroContent="Powerful tools to connect your prospects, clients, and team with your<br class='desktop-only' /> home service business."
      heroImgData={heroImgData}
      hasPricingCta
      hasTrustpilot
    />
  );

  const SliderData = [
    {
      title: "Services",
      content:
        "Your virtual receptionists do more than just answer calls.<br class='desktop-only' /> Get extra services included in your plan.",
      list: [
        "24/7 call and chat answering",
        "After-hours answering",
        "Lead qualification",
        "Bilingual answering",
      ],
      hasCta: true,
      cta: {
        href: "/services",
        text: "View all services",
      },
      image: {
        fileName: "tools-services-chat",
        altName: "Services chat",
        desktopWidth: "500",
      },
    },
    {
      title: "Industries",
      content:
        "Contractor receptionists with tailored solutions for your business and industry.",
      list: ["HVAC", "Restoration", "Plumbing", "Contractors"],
      hasCta: true,
      cta: {
        href: "/industries",
        text: "View all industries",
      },
      image: {
        fileName: "laying-wooden-flooring",
        altName: "Laying wooden flooring",
        isAlignBottom: false,
        desktopWidth: "500",
      },
    },
    {
      title: "Integrations",
      content:
        "Connect AnswerForce receptionists to your job management software, CRM tools, marketing platforms, and more.",
      list: ["ServiceTitan", "Housecall Pro", "Workiz", "Zapier"],
      hasCta: true,
      cta: {
        href: "/integrations",
        text: "View all integrations",
      },
      image: {
        fileName: "integrations-grid",
        altName: "integration icons",
        isAlignBottom: false,
        desktopWidth: "500",
      },
    },
  ];

  const toolsData = [
    {
      title: "Scheduling software",
      link: "/answering-services/appointment-setting",
      image: "scheduling-software-screen",
      items: [
        "Let clients book online",
        "Linked to your calendar",
        "Save time scheduling estimates",
      ],
    },
    {
      title: "Mobile app",
      link: "/app",
      image: "available-mobile-app-status",
      items: [
        "See new calls and leads instantly",
        "Set your availability to take calls",
        "Message your team on the go",
      ],
    },
    {
      title: "Business phone line",
      link: "/business-phone-line",
      image: "phone-line-screen",
      items: [
        "Two numbers, one cell phone",
        "Call and message clients easily",
        "Easily set your availability for calls",
      ],
    },
    {
      title: "Live chat",
      link: "/live-chat",
      image: "live-chat-screen",
      items: [
        "Use on your website and Facebook",
        'Turn "just looking" to leads with ease',
        "Engage with website and Facebook visitors 24/7",
      ],
    },
  ];

  return (
    <Theme mainClassName="main" hasSidebar hero={hero} seo={seoData}>
      <FeatureSectionTwocol
        title="Growth-oriented business solutions<span class='dot'>.</span>"
        image={{
          fileName: "plumbing-technician-repairing",
          altText: "Plumbing Technician Repairing",
          desktopWidth: 500,
          mobileWidth: 335,
        }}
        bgColor="white"
        content={[
          "Communication is much more than picking up the phone. Your AnswerForce account comes with a suite of tools to help you save time and win jobs every day.",
          "When it comes to efficiency and flexibility, cloud-based services lead the way. They're agile and dependable. ",
        ]}
      />
      {isMobile ? (
        <ProgressBarSwiper
          slideData={toolsData}
          title="Tools built to help<br class='desktop-only' /> your business grow<span class='dot'>.</span>"
          description="Compete with the best in the business. Powerful tools to win jobs faster."
        />
      ) : (
        <ToolsSection
          toolsData={toolsData}
          title="Tools built to help<br class='desktop-only' /> your business grow<span class='dot'>.</span>"
          description="Compete with the best in the business. Powerful tools to win jobs faster."
        />
      )}
      <Slider
        data={SliderData}
        bgColor="white"
        variant="solutions"
        title="Plans that include more<span class='dot'>.</span>"
        subTitle="Solutions built to grow your business. Extend your reach with answering services,<br class='desktop-only' /> software tools, and integrations all included in your plan."
      />
      <AudioLayout
        title="Hear how <br class='desktop-only'>we <br class='phone-only'>sound"
        image={{
          fileName: "refer-friend-female-receptionist",
          altText: "Female receptionist",
          desktopWidth: 348,
          mobileWidth: 308,
        }}
      />
      <Trustpilot title="In our customers&#8217; words" variant="carousel" />
      <Discover
        bgColor="white"
        title="Begin your journey to 24/7 growth<span class='dot'>.</span>"
        color="black"
        cta="See Prices"
        href={
          IS_LIVE
            ? "https://www.answerforce.com/register"
            : "https://testing.answerforce.com/register"
        }
      />
    </Theme>
  );
}
